import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSafetyPlan } from "../../../store/slices/safetyPlansSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function SafetyPlanPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const safetyState = useSelector((state) => state.safetyPlans || {});
    const { currentSafetyPlan, isLoading, error } = safetyState;

    useEffect(() => {
        if (id) dispatch(fetchSafetyPlan(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentSafetyPlan) {
            const timer = setTimeout(() => window.print(), 800);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentSafetyPlan, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Safety Plan...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentSafetyPlan) return <div className="p-8 text-center">No Safety Plan record found.</div>;

    const d = currentSafetyPlan;

    return (
        <>
            <style jsx global>{`
                * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; }
                .print-container { padding: 40px !important; position: relative !important; }
                .logo { position: absolute !important; top: 20px !important; left: 20px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 30px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.6rem !important; font-weight: bold !important; margin-bottom: 5px !important; }
                .header h6 { font-size: 1.1rem !important; text-transform: uppercase !important; }

                .step-container { margin-bottom: 20px !important; border: 1px solid #000 !important; }
                .step-header { background: #f2f2f2 !important; padding: 8px 12px !important; border-bottom: 1px solid #000 !important; font-weight: bold !important; font-size: 1rem !important; color: #000 !important; }
                .step-body { padding: 12px !important; font-size: 0.95rem !important; line-height: 1.6 !important; min-height: 60px !important; }
                
                .social-item { margin-bottom: 8px !important; padding-bottom: 5px !important; border-bottom: 1px dashed #ccc !important; }
                .social-item:last-child { border-bottom: none !important; }

                .reason-living-box { border: 2px solid #000 !important; margin-top: 20px !important; padding: 15px !important; background: #fafafa !important; }
                .reason-label { font-weight: bold !important; text-decoration: underline !important; display: block !important; margin-bottom: 10px !important; }

                .footer { margin-top: 40px !important; border-top: 1px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 0.85rem !important; }
                
                .sig-section { display: flex !important; justify-content: space-between !important; margin-top: 50px !important; padding: 0 20px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 250px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }

                @media print {
                    .step-container { page-break-inside: avoid !important; }
                }
            `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>PATIENT SAFETY PLAN</h6>
                </div>

                {/* Step 1 */}
                <div className="step-container">
                    <div className="step-header">Step 1: Warning Signs</div>
                    <div className="step-body">
                        {d.warning_signs || d.warningSigns || "No warning signs listed."}
                    </div>
                </div>

                {/* Step 2 */}
                <div className="step-container">
                    <div className="step-header">Step 2: Internal Coping Strategies</div>
                    <div className="step-body">
                        {d.internal_coping || d.internalCoping || "No strategies listed."}
                    </div>
                </div>

                {/* Step 3 - Array mapping */}
                <div className="step-container">
                    <div className="step-header">Step 3: Social Distraction (People and Places)</div>
                    <div className="step-body">
                        {d.social_distractions && d.social_distractions.length > 0 ? (
                            d.social_distractions.map((item, index) => (
                                <div key={index} className="social-item">
                                    <strong>{index + 1}. {item.name || "Name not provided"}</strong> — {item.contact || "No contact info"}
                                </div>
                            ))
                        ) : (
                            "No social distractions listed."
                        )}
                    </div>
                </div>

                {/* Step 4 */}
                <div className="step-container">
                    <div className="step-header">Step 4: People I Can Ask for Help</div>
                    <div className="step-body">
                        {d.people_help || d.peopleHelp || "No support persons listed."}
                    </div>
                </div>

                {/* Step 5 */}
                <div className="step-container">
                    <div className="step-header">Step 5: Professional Help</div>
                    <div className="step-body">
                        {d.professionals || "No professional contacts listed."}
                    </div>
                </div>

                {/* Step 6 */}
                <div className="step-container">
                    <div className="step-header">Step 6: Safe Environment</div>
                    <div className="step-body">
                        {d.safe_environment || d.safeEnvironment || "No environment safety steps listed."}
                    </div>
                </div>

                {/* Reason for Living */}
                <div className="reason-living-box">
                    <span className="reason-label">My Most Important Reason for Living:</span>
                    <p style={{ fontStyle: 'italic', fontSize: '1.1rem' }}>
                        "{d.reason_living || d.reasonLiving || "Not specified."}"
                    </p>
                </div>

                {/* Signatures */}
                <div className="sig-section">
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Staff / Clinician Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}